import axios from "axios";
import { API_BASE_URL } from "../config/constants";

export const getCookie = (name) => {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  } catch {
    return null;
  }
};

export const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp < Math.floor(Date.now() / 1000);
};

/**
 * Clear all auth state from localStorage (single source of truth).
 */
export const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("userFirstName");
  localStorage.removeItem("userRole");
  localStorage.removeItem("isLoggedIn");
};

/**
 * Refresh the access token using the stored refresh token.
 *
 * This is the SINGLE shared refresh implementation for the whole app.
 * - Sends the refresh token via `Authorization: Bearer` (works on Safari /
 *   cross-domain where cookies are blocked).
 * - Reads the rotated tokens from the API response body (the backend rotates
 *   the refresh token on every call) and persists BOTH to localStorage, so the
 *   next refresh uses the new refresh token.
 *
 * @returns {Promise<string|null>} the new access token, or null on failure.
 */
export const refreshAccessToken = async () => {
  try {
    const refreshTokenValue = localStorage.getItem("refreshToken");
    if (!refreshTokenValue) return null;

    const response = await axios.post(
      `${API_BASE_URL}/refresh`,
      {},
      {
        headers: { Authorization: `Bearer ${refreshTokenValue}` },
        withCredentials: true,
      }
    );

    // Backend shape: { success, message, data: { accessToken, refreshToken, userId, firstName, role } }
    const data = response.data?.data || response.data || {};
    const newAccessToken = data.accessToken || data.jwt;
    const newRefreshToken = data.refreshToken;
    if (!newAccessToken) return null;

    localStorage.setItem("token", newAccessToken);
    // Persist the rotated refresh token — critical, the old one is now invalid
    if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);

    const payload = decodeJwtPayload(newAccessToken);
    if (payload?.userId) {
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userId", String(payload.userId));
      localStorage.setItem("userFirstName", payload.firstName || "");
      const role = payload.userRole || payload.role;
      if (role) localStorage.setItem("userRole", String(role));
    }

    return newAccessToken;
  } catch (error) {
    return null;
  }
};

/**
 * Check if the current authenticated user has Admin access.
 * Checks localStorage userRole and active JWT token claims.
 */
export const isUserAdmin = () => {
  try {
    const storedRole = localStorage.getItem("userRole") || (typeof getCookie === "function" ? getCookie("userRole") : null);
    if (storedRole && storedRole.toLowerCase().includes("admin")) return true;

    const token = localStorage.getItem("token") || (typeof getCookie === "function" ? (getCookie("jwtToken") || getCookie("token")) : null);
    if (!token) return false;
    const payload = decodeJwtPayload(token);
    if (!payload) return false;

    const role = payload.role || payload.userRole || payload.roles || "";
    if (typeof role === "string" && role.toLowerCase().includes("admin")) return true;
    if (Array.isArray(role) && role.some((r) => typeof r === "string" && r.toLowerCase().includes("admin"))) return true;
    if (Array.isArray(payload.authorities) && payload.authorities.some((a) => (a.authority || a)?.toLowerCase().includes("admin"))) return true;

    return false;
  } catch {
    return false;
  }
};

